@if (inputData$ | async; as inputData) {
  <div
    class="max-w-xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 p-1"
  >
    <div class="text-center space-y-2 mb-8">
      <h2 class="text-2xl font-bold text-gray-800">Create Assignment</h2>
      <p class="text-gray-500">
        Over 100,000 assignments have already been created by teachers on
        JustTeach.
      </p>
    </div>

    <div
      class="bg-white p-6 md:p-8 rounded-2xl shadow-lg shadow-gray-100 border border-gray-100 space-y-6"
    >
      <!-- Grade Selection -->
      <div>
        <label class="block text-sm font-bold text-gray-700 mb-1">Grade</label>
        <p class="text-xs text-gray-500 mb-2">
          Select the grade level for your assessment.
        </p>
        <div class="flex flex-wrap gap-2">
          @for (grade of visibleGrades$ | async; track grade.value) {
            <button
              type="button"
              (click)="updateGrade(grade.value)"
              class="px-4 py-2 rounded-full text-sm font-bold transition-all duration-200 border cursor-pointer"
              [class.text-white]="inputData.grade === grade.value"
              [class.shadow-md]="inputData.grade === grade.value"
              [class.bg-white]="inputData.grade !== grade.value"
              [class.text-gray-600]="inputData.grade !== grade.value"
              [class.border-gray-200]="inputData.grade !== grade.value"
              [class.hover:border-gray-300]="inputData.grade !== grade.value"
              [class.hover:bg-gray-50]="inputData.grade !== grade.value"
              [style.backgroundColor]="
                inputData.grade === grade.value ? themeColor : ''
              "
              [style.borderColor]="
                inputData.grade === grade.value ? themeColor : ''
              "
            >
              {{ grade.label }}
            </button>
          }
          @if (hasHiddenGrades$ | async) {
            <button
              type="button"
              (click)="toggleShowMoreGrades()"
              class="px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-600 cursor-pointer flex items-center gap-1"
            >
              @if (showMoreGrades$ | async) {
                <span>Show less</span>
                <lucide-icon
                  [img]="icons.ChevronUp"
                  class="w-4 h-4"
                ></lucide-icon>
              } @else {
                <span>K-5</span>
                <lucide-icon
                  [img]="icons.ChevronDown"
                  class="w-4 h-4"
                ></lucide-icon>
              }
            </button>
          }
        </div>
      </div>

      <!-- Tier 1 Subject Selection (Category) -->
      @if (inputData.grade !== null) {
        <div class="animate-in">
          <label class="block text-sm font-bold text-gray-700 mb-1"
            >Subject Area</label
          >
          <p class="text-xs text-gray-500 mb-2">
            Select a subject category for your assessment.
          </p>
          <div class="flex flex-wrap gap-2">
            @for (tier1 of visibleTier1Subjects$ | async; track tier1.id) {
              <button
                type="button"
                (click)="updateTier1Subject(tier1.id)"
                class="px-4 py-2 rounded-full text-sm font-bold transition-all duration-200 border cursor-pointer text-left w-fit"
                [class.text-white]="inputData.tier1SubjectId === tier1.id"
                [class.shadow-md]="inputData.tier1SubjectId === tier1.id"
                [class.bg-white]="inputData.tier1SubjectId !== tier1.id"
                [class.text-gray-600]="
                  inputData.tier1SubjectId !== tier1.id && tier1.isRelevant
                "
                [class.text-gray-400]="
                  inputData.tier1SubjectId !== tier1.id && !tier1.isRelevant
                "
                [class.border-gray-200]="
                  inputData.tier1SubjectId !== tier1.id && tier1.isRelevant
                "
                [class.border-gray-100]="
                  inputData.tier1SubjectId !== tier1.id && !tier1.isRelevant
                "
                [class.hover:border-gray-300]="
                  inputData.tier1SubjectId !== tier1.id
                "
                [class.hover:bg-gray-50]="
                  inputData.tier1SubjectId !== tier1.id
                "
                [class.opacity-60]="!tier1.isRelevant"
                [style.backgroundColor]="
                  inputData.tier1SubjectId === tier1.id ? themeColor : ''
                "
                [style.borderColor]="
                  inputData.tier1SubjectId === tier1.id ? themeColor : ''
                "
              >
                {{ tier1.category }}
              </button>
            }
            @if (hasHiddenTier1$ | async) {
              <button
                type="button"
                (click)="toggleShowMoreTier1()"
                class="px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-600 cursor-pointer flex items-center gap-1"
              >
                @if (showMoreTier1$ | async) {
                  <span>Show less</span>
                  <lucide-icon
                    [img]="icons.ChevronUp"
                    class="w-4 h-4"
                  ></lucide-icon>
                } @else {
                  <span>See more</span>
                  <lucide-icon
                    [img]="icons.ChevronDown"
                    class="w-4 h-4"
                  ></lucide-icon>
                }
              </button>
            }
          </div>
        </div>
      }

      <!-- Tier 2 Subject Selection (Specific Subject) — hidden when advanced context fields are disabled -->
      @if (
        inputData.tier1SubjectId &&
        (enableAdvancedContextFields$ | async)
      ) {
        <div class="animate-in">
          <label class="block text-sm font-bold text-gray-700 mb-1"
            >Specific Subject</label
          >
          <p class="text-xs text-gray-500 mb-2">
            Choose a specific topic within your subject area.
          </p>
          <div class="flex flex-wrap gap-2">
            @for (tier2 of visibleTier2Subjects$ | async; track tier2.id) {
              <button
                type="button"
                (click)="updateTier2Subject(tier2.id)"
                class="px-4 py-2 rounded-full text-sm font-bold transition-all duration-200 border cursor-pointer text-left w-fit"
                [class.text-white]="inputData.tier2SubjectId === tier2.id"
                [class.shadow-md]="inputData.tier2SubjectId === tier2.id"
                [class.bg-white]="inputData.tier2SubjectId !== tier2.id"
                [class.text-gray-600]="
                  inputData.tier2SubjectId !== tier2.id && tier2.isRelevant
                "
                [class.text-gray-400]="
                  inputData.tier2SubjectId !== tier2.id && !tier2.isRelevant
                "
                [class.border-gray-200]="
                  inputData.tier2SubjectId !== tier2.id && tier2.isRelevant
                "
                [class.border-gray-100]="
                  inputData.tier2SubjectId !== tier2.id && !tier2.isRelevant
                "
                [class.hover:border-gray-300]="
                  inputData.tier2SubjectId !== tier2.id
                "
                [class.hover:bg-gray-50]="
                  inputData.tier2SubjectId !== tier2.id
                "
                [class.opacity-60]="!tier2.isRelevant"
                [style.backgroundColor]="
                  inputData.tier2SubjectId === tier2.id ? themeColor : ''
                "
                [style.borderColor]="
                  inputData.tier2SubjectId === tier2.id ? themeColor : ''
                "
              >
                {{ tier2.name }}
              </button>
            }
            @if (hasHiddenTier2$ | async) {
              <button
                type="button"
                (click)="toggleShowMoreTier2()"
                class="px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-600 cursor-pointer flex items-center gap-1"
              >
                @if (showMoreTier2$ | async) {
                  <span>Show less</span>
                  <lucide-icon
                    [img]="icons.ChevronUp"
                    class="w-4 h-4"
                  ></lucide-icon>
                } @else {
                  <span>See more</span>
                  <lucide-icon
                    [img]="icons.ChevronDown"
                    class="w-4 h-4"
                  ></lucide-icon>
                }
              </button>
            }
          </div>
        </div>
      }

      <!-- US State Selection — hidden when advanced context fields are disabled -->
      @if (enableAdvancedContextFields$ | async) {
        <div>
          <label class="block text-sm font-bold text-gray-700 mb-1"
            >State</label
          >
          <p class="text-xs text-gray-500 mb-2">
            Select your state for relevant educational standards.
          </p>
          <div class="relative" #stateCombobox>
            <div
              class="relative flex items-center w-full border rounded-xl transition-all duration-200 cursor-pointer"
              [class.border-gray-200]="!(stateDropdownOpen$ | async)"
              [class.ring-2]="(stateDropdownOpen$ | async)"
              [style.borderColor]="
                (stateDropdownOpen$ | async) ? themeColor : ''
              "
              [style.--tw-ring-color]="themeColor + '33'"
              (click)="openStateDropdown()"
            >
              <span class="ml-4 shrink-0 flex items-center">
                <lucide-icon
                  [img]="icons.MapPin"
                  class="w-5 h-5 text-gray-400"
                ></lucide-icon>
              </span>
              @if (inputData.state) {
                <div
                  class="flex-1 flex items-center justify-between py-3 pl-3 pr-3"
                >
                  <span class="text-gray-800 font-medium">{{
                    selectedStateName$ | async
                  }}</span>
                  <button
                    type="button"
                    (click)="clearState($event)"
                    class="p-1 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <lucide-icon
                      [img]="icons.X"
                      class="w-4 h-4 text-gray-400"
                    ></lucide-icon>
                  </button>
                </div>
              } @else {
                <input
                  type="text"
                  placeholder="Search for your state..."
                  class="flex-1 min-w-0 py-3 pl-3 pr-3 bg-transparent border-none outline-none text-gray-800 placeholder-gray-400"
                  [value]="stateSearchText$ | async"
                  (input)="onStateSearchInput($event)"
                  (focus)="openStateDropdown()"
                  (blur)="closeStateDropdown()"
                />
                <lucide-icon
                  [img]="icons.ChevronDown"
                  class="w-5 h-5 text-gray-400 mr-3 shrink-0"
                ></lucide-icon>
              }
            </div>

            @if (stateDropdownOpen$ | async) {
              <div
                class="absolute z-50 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto animate-in fade-in duration-200"
                [class.mt-1]="!(stateDropdownOpenUp$ | async)"
                [class.mb-1]="(stateDropdownOpenUp$ | async)"
                [class.bottom-full]="(stateDropdownOpenUp$ | async)"
                [class.top-full]="!(stateDropdownOpenUp$ | async)"
              >
                @for (state of filteredStates$ | async; track state.code) {
                  <button
                    type="button"
                    class="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3 cursor-pointer"
                    [class.bg-green-50]="inputData.state === state.code"
                    (mousedown)="selectState(state)"
                  >
                    <span class="text-gray-500 text-sm font-mono w-8">{{
                      state.code
                    }}</span>
                    <span class="text-gray-800">{{ state.name }}</span>
                  </button>
                } @empty {
                  <div class="px-4 py-3 text-gray-500 text-sm">
                    No states found
                  </div>
                }
              </div>
            }
          </div>
        </div>
      }

      <!-- Error Message -->
      @if (error$ | async; as error) {
        <div
          class="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"
        >
          {{ error }}
        </div>
      }
    </div>

    <!-- Spacer for fixed footer -->
    <div class="h-24"></div>
  </div>

  <!-- Fixed Footer -->
  <div class="step-footer">
    <div class="max-w-xl mx-auto flex gap-3 px-1">
      @if (isAuthenticated$ | async) {
        <button
          (click)="onExit()"
          class="flex-1 py-3 text-gray-500 font-medium bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors cursor-pointer"
        >
          Exit
        </button>
      }
      <button
        (click)="onContinue()"
        [disabled]="
          inputData.grade === null ||
          !inputData.tier1SubjectId ||
          ((enableAdvancedContextFields$ | async) &&
            (!inputData.tier2SubjectId || !inputData.state))
        "
        class="py-3 text-white font-bold rounded-xl shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        [class.flex-1]="!(isAuthenticated$ | async)"
        [class.flex-[2]]="(isAuthenticated$ | async)"
        [style.backgroundColor]="themeColor"
      >
        Continue
      </button>
    </div>
  </div>
}
